import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { StyledButton, StyledTextInput } from 'components/shared';
import { EXPIRED_ACTION_CODE, INVALID_ACTION_CODE, USER_NOT_FOUND } from 'constants/errorCodes';
import { LOGIN } from 'constants/routes';
import { useAuth } from 'context/auth/AuthContextProvider';
import { useForm } from 'hooks/useForm';
import { Banner } from 'storybook';
import { resetPasswordFormValidations } from 'utils/validation/resetPasswordFormValidation';

import styles from './ResetPasswordForm.module.css';

const initialState = { newPassword: '', repeatedPassword: '' };

const ResetPasswordForm = () => {
   const { t } = useTranslation();
   const { resetPassword } = useAuth();
   const navigate = useNavigate();
   const [searchParams] = useSearchParams();
   const [resetPasswordError, setResetPasswordError] = useState('');
   const [loading, setLoading] = useState(false);

   const errorMessages = new Map([
      [USER_NOT_FOUND, t('reset_password.errors.user_not_found')],
      [EXPIRED_ACTION_CODE, t('reset_password.errors.expired')],
      [INVALID_ACTION_CODE, t('reset_password.errors.invalid')],
   ]);

   const handleSubmit = () => {
      setResetPasswordError('');
      if (values.newPassword !== values.repeatedPassword) {
         setResetPasswordError(t('reset_password.errors.passwords_not_equal'));
         return;
      }

      const oobCode = searchParams.get('oobCode');
      if (!oobCode) {
         setResetPasswordError(t('reset_password.errors.missing_code'));
         return;
      }

      setLoading(true);
      resetPassword(oobCode, values.newPassword)
         .then(() => navigateToLoginPage())
         .catch((error: Error) => {
            const errorMessage = errorMessages.get(error.message);
            setResetPasswordError(errorMessage || t('reset_password.errors.generic'));
         })
         .finally(() => setLoading(false));
   };

   const { values, changeHandler, touched, submitHandler, errorsList } = useForm({
      initialState: initialState,
      validations: resetPasswordFormValidations,
      onSubmit: handleSubmit,
   });

   const navigateToLoginPage = () =>
      navigate(LOGIN, { replace: true, state: { successPasswordReset: true } });

   return (
      <div className={styles.mainContainer}>
         <h1>{t('reset_password.title')}</h1>
         {resetPasswordError && (
            <Banner
               children={resetPasswordError}
               variant="error"
               fullWidth
               className={styles.banner}
            />
         )}
         <form noValidate onSubmit={submitHandler}>
            <StyledTextInput
               name="newPassword"
               value={values.newPassword}
               type="password"
               onChange={changeHandler}
               label={t('reset_password.form.new_password')}
               error={touched.newPassword && errorsList.newPassword}
               helperText={touched.newPassword && errorsList.newPassword}
               className={styles.input}
            />
            <StyledTextInput
               name="repeatedPassword"
               value={values.repeatedPassword}
               type="password"
               onChange={changeHandler}
               label={t('reset_password.form.repeat_password')}
               error={touched.repeatedPassword && errorsList.repeatedPassword}
               helperText={touched.repeatedPassword && errorsList.repeatedPassword}
               className={styles.input}
            />
            <StyledButton
               type="submit"
               text={t('reset_password.form.submit_button')}
               variant="filled-primary"
               className={styles.button}
               loading={loading}
            />
         </form>
      </div>
   );
};

export default ResetPasswordForm;
